// LEGACY translation manager — the pre-M2 `translation_keys` / draft-publish
// model. Superseded by /translations (the M2 `translations` table, motiflink
// §1.5) and deliberately UNLINKED from the nav: two near-identical editors in
// the sidebar invites editing the wrong one.
//
// Kept compiling and reachable by URL because the old rows still exist and the
// keys-manifest CI still reads them. Retired — along with `translation_keys`,
// the manifest, and infra/d1-sync — at the M9 cutover (replan-v3 §M2).

import { Badge, type BadgeTone, Button, Input, Segmented, Textarea } from '@fajrarabi/ui';
import type {
  TranslationKeyDto,
  TranslationNamespaceDto,
  TranslationStringDto,
} from '@fajrarabi/zod-schemas/translations';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { listKeys, listNamespaces, publishStrings, upsertString } from '~/lib/translations';

type FilterMode = 'all' | 'missing' | 'draft';

export const Route = createFileRoute('/_authed/translations-legacy')({
  component: LegacyTranslationsPage,
});

function LegacyTranslationsPage() {
  const [namespace, setNamespace] = useState<string | undefined>(undefined);
  const [filter, setFilter] = useState<FilterMode>('all');
  const [search, setSearch] = useState('');
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const namespacesQuery = useQuery({
    queryKey: ['translations', 'namespaces'],
    queryFn: ({ signal }) => listNamespaces(signal),
    staleTime: 60_000,
  });

  const keysQuery = useQuery({
    queryKey: ['translations', 'keys', namespace, filter, search],
    queryFn: ({ signal }) =>
      listKeys({ namespace, filter, search: search.trim() || undefined }, signal),
    staleTime: 10_000,
  });

  const activeRecord: TranslationKeyDto | null = useMemo(
    () => keysQuery.data?.keys.find((k) => k.key === activeKey) ?? null,
    [keysQuery.data, activeKey],
  );

  return (
    <div className="flex h-full flex-col">
      <p className="flex flex-wrap items-center gap-2 border-b border-sun/30 bg-sun/10 px-5 py-3 text-sm text-ink-700">
        <span className="font-semibold">Legacy editor.</span>
        This edits the old <span className="font-mono text-xs">translation_keys</span> tables, kept
        until the M9 cutover.
        <Link to="/translations" className="font-semibold text-brand-700 underline">
          Use the current Translations page
        </Link>
        for live strings.
      </p>
      <div className="grid min-h-0 flex-1 grid-cols-[16rem_1fr_28rem]">
        <NamespaceList
          namespaces={namespacesQuery.data ?? []}
          loading={namespacesQuery.isLoading}
          active={namespace}
          onSelect={(ns) => {
            setNamespace(ns);
            setActiveKey(null);
          }}
        />
        <div className="flex flex-col border-x border-burgundy-500/10">
          <Toolbar
            filter={filter}
            onFilterChange={setFilter}
            search={search}
            onSearchChange={setSearch}
            total={keysQuery.data?.total}
            shown={keysQuery.data?.keys.length}
          />
          <KeyList
            keys={keysQuery.data?.keys ?? []}
            loading={keysQuery.isLoading}
            activeKey={activeKey}
            onPick={setActiveKey}
          />
        </div>
        <Editor record={activeRecord} />
      </div>
    </div>
  );
}

// ─── Sidebar: namespaces ───────────────────────────────────────────────────

function NamespaceList({
  namespaces,
  loading,
  active,
  onSelect,
}: {
  namespaces: TranslationNamespaceDto[];
  loading: boolean;
  active: string | undefined;
  onSelect: (ns: string | undefined) => void;
}) {
  return (
    <aside className="flex flex-col overflow-auto bg-cream-100/60 p-4">
      <h2 className="px-3 pb-3 text-xs font-semibold uppercase tracking-wider text-ink-500">
        Namespaces
      </h2>
      <button
        type="button"
        onClick={() => onSelect(undefined)}
        className={`text-start rounded-2xl px-3 py-2 text-sm font-medium transition-colors ${
          active === undefined ? 'bg-burgundy-500 text-cream-50' : 'text-ink-700 hover:bg-cream-200'
        }`}
      >
        All
      </button>
      {loading && <div className="px-3 py-2 text-sm text-ink-500">Loading…</div>}
      {namespaces.map((ns) => (
        <button
          key={ns.namespace}
          type="button"
          onClick={() => onSelect(ns.namespace)}
          className={`mt-1 flex items-center justify-between rounded-2xl px-3 py-2 text-start text-sm font-medium transition-colors ${
            active === ns.namespace
              ? 'bg-burgundy-500 text-cream-50'
              : 'text-ink-700 hover:bg-cream-200'
          }`}
        >
          <span className="truncate">{ns.namespace}</span>
          <span
            className={`ms-2 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
              active === ns.namespace ? 'bg-cream-50/20 text-cream-50' : 'bg-cream-200 text-ink-500'
            }`}
          >
            {ns.totalKeys}
          </span>
        </button>
      ))}
    </aside>
  );
}

// ─── Toolbar above the key list ────────────────────────────────────────────

function Toolbar({
  filter,
  onFilterChange,
  search,
  onSearchChange,
  total,
  shown,
}: {
  filter: FilterMode;
  onFilterChange: (f: FilterMode) => void;
  search: string;
  onSearchChange: (s: string) => void;
  total: number | undefined;
  shown: number | undefined;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-burgundy-500/10 bg-cream-50 px-5 py-4">
      <Segmented
        size="sm"
        ariaLabel="Filter translation keys"
        value={filter}
        onChange={onFilterChange}
        options={[
          { value: 'all', label: 'All' },
          { value: 'missing', label: 'Missing' },
          { value: 'draft', label: 'Draft' },
        ]}
      />
      <Input
        type="search"
        placeholder="Search key or description…"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        className="ms-auto h-9 w-72 bg-white text-sm"
      />
      <div className="text-xs text-ink-500">
        {shown !== undefined ? `${shown} shown` : '—'}
        {total !== undefined ? ` · ${total} total` : ''}
      </div>
    </div>
  );
}

// ─── Key list (middle column) ──────────────────────────────────────────────

function KeyList({
  keys,
  loading,
  activeKey,
  onPick,
}: {
  keys: TranslationKeyDto[];
  loading: boolean;
  activeKey: string | null;
  onPick: (k: string) => void;
}) {
  if (loading) {
    return <div className="p-6 text-sm text-ink-500">Loading…</div>;
  }
  if (keys.length === 0) {
    return <div className="p-6 text-sm text-ink-500">No keys match the current filter.</div>;
  }
  return (
    <ul className="flex-1 overflow-auto">
      {keys.map((k) => {
        const ar = k.strings.find((s) => s.locale === 'ar');
        const en = k.strings.find((s) => s.locale === 'en');
        const arStatus = ar?.status ?? 'missing';
        const enStatus = en?.status ?? 'missing';
        const isActive = activeKey === k.key;
        return (
          <li key={k.key}>
            <button
              type="button"
              onClick={() => onPick(k.key)}
              className={`flex w-full flex-col gap-1 border-b border-burgundy-500/5 px-5 py-3 text-start transition-colors ${
                isActive ? 'bg-cream-100' : 'hover:bg-cream-100/60'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-ink-700">{k.key}</span>
                <span className="ms-auto flex gap-1">
                  <StatusPill locale="en" status={enStatus} />
                  <StatusPill locale="ar" status={arStatus} />
                </span>
              </div>
              {k.description && <div className="text-xs text-ink-500">{k.description}</div>}
              <div className="flex gap-3 text-sm">
                <span className="flex-1 truncate text-ink-900">{en?.value ?? '—'}</span>
                <span className="flex-1 truncate text-ink-900" dir="rtl">
                  {ar?.value ?? '—'}
                </span>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

const statusToneMap: Record<'draft' | 'published' | 'missing', BadgeTone> = {
  published: 'success',
  draft: 'warning',
  missing: 'danger',
};

function StatusPill({
  locale,
  status,
}: {
  locale: 'ar' | 'en';
  status: 'draft' | 'published' | 'missing';
}) {
  return (
    <Badge tone={statusToneMap[status]}>
      {locale} · {status}
    </Badge>
  );
}

// ─── Editor (right column) ─────────────────────────────────────────────────

function Editor({ record }: { record: TranslationKeyDto | null }) {
  const queryClient = useQueryClient();
  const upsertMutation = useMutation({
    mutationFn: upsertString,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['translations', 'keys'] });
    },
  });
  const publishMutation = useMutation({
    mutationFn: publishStrings,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['translations', 'keys'] });
    },
  });

  if (!record) {
    return (
      <aside className="flex h-full items-center justify-center bg-cream-50 p-6 text-sm text-ink-500">
        Pick a key on the left to edit it.
      </aside>
    );
  }

  const ar = record.strings.find((s) => s.locale === 'ar');
  const en = record.strings.find((s) => s.locale === 'en');

  return (
    <aside className="flex h-full flex-col overflow-auto bg-cream-50 p-6">
      <header>
        <div className="font-mono text-xs text-ink-500">{record.namespace}</div>
        <h2 className="mt-1 font-display text-xl font-bold text-ink-900">{record.key}</h2>
        {record.description && <p className="mt-1 text-sm text-ink-700">{record.description}</p>}
        {record.maxLength > 0 && (
          <p className="mt-1 text-xs text-ink-500">Max length: {record.maxLength}</p>
        )}
      </header>

      <div className="mt-5 flex-1 space-y-5">
        <LocaleEditor
          keyName={record.key}
          locale="en"
          dir="ltr"
          maxLength={record.maxLength}
          current={en}
          onSave={(value, status) =>
            upsertMutation.mutateAsync({ key: record.key, locale: 'en', value, status })
          }
          saving={upsertMutation.isPending && upsertMutation.variables?.locale === 'en'}
        />
        <LocaleEditor
          keyName={record.key}
          locale="ar"
          dir="rtl"
          maxLength={record.maxLength}
          current={ar}
          onSave={(value, status) =>
            upsertMutation.mutateAsync({ key: record.key, locale: 'ar', value, status })
          }
          saving={upsertMutation.isPending && upsertMutation.variables?.locale === 'ar'}
        />
      </div>

      <footer className="mt-6 flex items-center gap-2 border-t border-burgundy-500/10 pt-4">
        <Button
          size="sm"
          loading={publishMutation.isPending}
          loadingLabel="Publishing…"
          onClick={async () => {
            const ids = record.strings.filter((s) => s.status === 'draft').map((s) => s.id);
            if (ids.length === 0) return;
            await publishMutation.mutateAsync({ ids });
          }}
        >
          Publish all drafts on this key
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled
          title="AI translation drafts will arrive when ANTHROPIC_API_KEY is provisioned."
        >
          Generate with AI · soon
        </Button>
      </footer>
    </aside>
  );
}

function LocaleEditor({
  keyName,
  locale,
  dir,
  maxLength,
  current,
  onSave,
  saving,
}: {
  keyName: string;
  locale: 'ar' | 'en';
  dir: 'ltr' | 'rtl';
  maxLength: number;
  current: TranslationStringDto | undefined;
  onSave: (value: string, status: 'draft' | 'published') => Promise<unknown>;
  saving: boolean;
}) {
  const [value, setValue] = useState(current?.value ?? '');
  // Re-seed when the upstream record changes.
  const lastKey = `${keyName}:${locale}:${current?.value ?? ''}`;
  const [seedKey, setSeedKey] = useState(lastKey);
  if (seedKey !== lastKey) {
    setSeedKey(lastKey);
    setValue(current?.value ?? '');
  }

  const overLimit = maxLength > 0 && value.length > maxLength;

  return (
    <div className="rounded-3xl bg-white p-4 ring-1 ring-burgundy-500/10">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-burgundy-500">
          {locale === 'ar' ? 'العربية' : 'English'}
        </span>
        {current && (
          <Badge tone={current.status === 'published' ? 'success' : 'warning'}>
            {current.status}
          </Badge>
        )}
      </div>
      <Textarea
        dir={dir}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={4}
        className="mt-2 text-sm"
      />
      <div className="mt-2 flex items-center justify-between text-xs">
        <span className={overLimit ? 'text-heart' : 'text-ink-500'}>
          {value.length}
          {maxLength > 0 ? ` / ${maxLength}` : ''}
        </span>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={saving || overLimit}
            onClick={() => onSave(value, 'draft')}
          >
            Save draft
          </Button>
          <Button
            size="sm"
            disabled={saving || overLimit}
            onClick={() => onSave(value, 'published')}
          >
            Publish
          </Button>
        </div>
      </div>
    </div>
  );
}
